import type { VenueDto } from './apiClient';
import { API_BASE_URL } from './httpClient';

export interface VenueDraft {
  address: string;
  latitude: number | null;
  longitude: number | null;
  placeId?: string | null;
}

export interface VenueSuggestion extends VenueDraft {
  id: string;
}

export const emptyVenueDraft = (): VenueDraft => ({
  address: '',
  latitude: null,
  longitude: null,
  placeId: null
});

export const normalizeVenueDraft = (venue: VenueDraft | null | undefined): VenueDraft => {
  if (!venue) {
    return emptyVenueDraft();
  }

  const address = venue.address?.trim() ?? '';
  const latitude = typeof venue.latitude === 'number' && Number.isFinite(venue.latitude) && venue.latitude >= -90 && venue.latitude <= 90
    ? venue.latitude
    : null;
  const longitude = typeof venue.longitude === 'number' && Number.isFinite(venue.longitude) && venue.longitude >= -180 && venue.longitude <= 180
    ? venue.longitude
    : null;

  return {
    address,
    latitude,
    longitude,
    placeId: venue.placeId?.trim() || null
  };
};

export const hasVenueCoordinates = (venue: VenueDraft | VenueDto | null | undefined): venue is VenueDraft & { latitude: number; longitude: number } => {
  return Boolean(
    venue
      && typeof venue.latitude === 'number'
      && Number.isFinite(venue.latitude)
      && venue.latitude >= -90
      && venue.latitude <= 90
      && typeof venue.longitude === 'number'
      && Number.isFinite(venue.longitude)
      && venue.longitude >= -180
      && venue.longitude <= 180
  );
};

export const toVenueDto = (venue: VenueDraft | null | undefined): VenueDto | null => {
  const normalized = normalizeVenueDraft(venue);
  if (!normalized.address || !hasVenueCoordinates(normalized)) {
    return null;
  }

  return {
    address: normalized.address,
    latitude: normalized.latitude,
    longitude: normalized.longitude,
    placeId: normalized.placeId ?? null
  };
};

export const getVenueDisplayAddress = (venue: VenueDraft | VenueDto | null | undefined): string => {
  return venue?.address?.trim() ?? '';
};

export const isCoordinateLikeAddress = (address: string | null | undefined): boolean => {
  if (!address) {
    return false;
  }

  return /^-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?$/.test(address.trim());
};

export const getPreferredVenueAddress = (
  venue: VenueDraft | VenueDto | null | undefined,
  fallbackLocation?: string | null
): string => {
  const address = getVenueDisplayAddress(venue);
  const fallback = fallbackLocation?.trim() ?? '';

  if (address && !isCoordinateLikeAddress(address)) {
    return address;
  }

  if (fallback && !isCoordinateLikeAddress(fallback)) {
    return fallback;
  }

  return address || fallback;
};

export const lookupVenueDisplayAddress = async (
  venue: VenueDraft | VenueDto | null | undefined,
  language: 'da' | 'en',
  signal?: AbortSignal
): Promise<string> => {
  const address = getVenueDisplayAddress(venue);

  if (!hasVenueCoordinates(venue) || (address && !isCoordinateLikeAddress(address))) {
    return address;
  }

  const params = new URLSearchParams({
    latitude: String(venue.latitude),
    longitude: String(venue.longitude),
    language
  });

  try {
    const response = await fetch(`${API_BASE_URL}/venues/reverse?${params.toString()}`, {
      method: 'GET',
      signal,
      credentials: 'include',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      return address;
    }

    const payload = await response.json() as {
      success?: boolean;
      data?: string | null;
    };

    return payload.data?.trim() || address;
  } catch {
    return address;
  }
};

export const buildGeoapifyStaticMapUrl = (venue: VenueDraft | VenueDto | null | undefined, zoom?: number): string | null => {
  if (!hasVenueCoordinates(venue)) {
    return null;
  }

  const normalizedZoom = typeof zoom === 'number' && Number.isFinite(zoom)
    ? Math.min(18, Math.max(9, Math.round(zoom)))
    : null;

  const params = new URLSearchParams({
    latitude: String(venue.latitude),
    longitude: String(venue.longitude)
  });

  if (normalizedZoom !== null) {
    params.set('zoom', String(normalizedZoom));
  }

  return `${API_BASE_URL}/venues/static-map?${params.toString()}`;
};

export const buildVenueMapLink = (venue: VenueDraft | VenueDto | null | undefined, zoom?: number): string | null => {
  if (!venue?.address?.trim()) {
    return null;
  }

  if (hasVenueCoordinates(venue)) {
    const normalizedZoom = typeof zoom === 'number' && Number.isFinite(zoom)
      ? Math.min(18, Math.max(9, Math.round(zoom)))
      : 14;
    return `https://www.openstreetmap.org/?mlat=${venue.latitude}&mlon=${venue.longitude}#map=${normalizedZoom}/${venue.latitude}/${venue.longitude}`;
  }

  return `https://www.openstreetmap.org/search?query=${encodeURIComponent(venue.address.trim())}`;
};

export const searchVenueSuggestions = async (
  query: string,
  language: 'da' | 'en',
  signal?: AbortSignal
): Promise<VenueSuggestion[]> => {
  const trimmedQuery = query.trim();
  if (trimmedQuery.length < 3) {
    return [];
  }

  const params = new URLSearchParams({
    query: trimmedQuery,
    language
  });

  const response = await fetch(`${API_BASE_URL}/venues/autocomplete?${params.toString()}`, {
    method: 'GET',
    signal,
    credentials: 'include',
    headers: {
      'X-Requested-With': 'XMLHttpRequest'
    }
  });

  if (!response.ok) {
    return [];
  }

  const payload = await response.json() as {
    success?: boolean;
    data?: VenueSuggestion[] | null;
  };

  return Array.isArray(payload.data)
    ? payload.data.filter((result) => Boolean(result.address?.trim()))
    : [];
};